import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BillingSettingCode,
  BillingSettings,
  objectEntries,
  PlanFeeLabel,
  PlanServiceName,
  PlanTierLabel,
} from 'vopay';

/**
 * Raw API value stays the logic key; only the rendered string is localized (i18n-conventions rule).
 * Code-keyed maps (`labelsByCode`) where a stable code maps 1:1 to a label; prose-keyed maps
 * (`feeLabels`/`serviceNames`/`values`) where the label is a shared vocabulary reused across ~100 per-rail
 * codes, or section/select values that have no separate code. Every resolver falls back to the raw string,
 * so an unmapped row renders English, not blank. Each map is a `Record` over a union (`BillingSettingCode`,
 * `PlanFeeLabel`, …), not `string`, so tsc catches a typo'd key or a new/forgotten member.
 * Blind spot: the union is a hand-maintained snapshot — a code earthnode adds before the union is updated
 * arrives at runtime and renders its English fallback, invisible to both i18n guards; browse in French to catch.
 */
/** `PlanFeeLabel`s deliberately left unmapped — only the empty label, which has nothing to translate. */
type UntranslatedFeeLabel = '';

/** `PlanServiceName`s deliberately left unmapped — pure wordmarks/brand names (Interac …, VoPay Instant™/Verify™, iQ11®, PayPal, …) render verbatim via the fallback. */
type UntranslatedServiceName =
  | 'Interac e-Transfer® Request Money'
  | 'Interac e-Transfer® Send'
  | 'VoPay Instant™'
  | 'Interac e-Transfer® Request Money - Embedded'
  | 'Inbound eTransfer'
  | 'PayPal - Withdrawal'
  | 'Venmo Withdrawal'
  | 'Zelle Withdrawal'
  | 'Credit Cards'
  | 'Global Send'
  | 'Apple Pay'
  | 'Google Pay'
  | 'eLinx'
  | 'VoPay Verify™ - Bank Verification'
  | 'VoPay Verify™- Basic Interac® Verification (Email)'
  | 'VoPay Verify™- Enhanced Interac® Verification (Email)'
  | 'VoPay Verify™ - Basic Card Verification'
  | 'VoPay Verify™ - Enhanced Card Verification'
  | 'VoPay Verify™ - Basic Email Verification'
  | 'VoPay Verify™ - Enhanced Email Verification'
  | 'VoPay Verify™ - Basic Phone Number Verification'
  | 'VoPay Verify™ - Enhanced Phone Number Verification'
  | 'VoPay Verify™ - Non--Doc ID Verification'
  | 'Branding'
  | 'ERP & Accounting'
  | 'ERP System'
  | 'iQ11 Standard'
  | 'IQ11 Standard'
  | 'IQ11 Boost'
  | 'IQ11 Plus'
  | 'IQ11 Advantage'
  | 'IQ11 Token'
  | 'IQ11 Third Party'
  | 'Interac Money Request'
  | 'Interac Bulk Payout'
  | 'VoPayInstant Withdraw'
  | 'Pay By Bank'
  | 'OCT'
  | 'AFT'
  | 'PayPal Withdrawal'
  | 'Credit Card'
  | 'Basic Interac Email Verifications'
  | 'Enhanced Interac Email Verifications';

type DisplayValueKey =
  | 'Pay As You Go'
  | 'Subscription'
  | 'Post Paid'
  | 'Per Transaction'
  | 'Specific'
  | 'Specific Day of the Month'
  | 'Beginning of the Month'
  | 'End of the Month'
  | 'Monthly'
  | 'Yearly'
  | 'Per Request'
  | 'Not Applied'
  | 'Vopay Account';

export default function useBillingPackageLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Billing.PackageDetails' });

  return useMemo(() => {
    // Agreement-details + account-configuration rows (the account Billing tab). Keyed by the row's
    // stable code (`item.settings`). The leaf keys and their en/fr values are unchanged from when this
    // map was keyed by prose — only the lookup keys moved from prose to code.
    const settingLabelsByCode: Record<BillingSettingCode, string> = {
      // Agreement / plan details.
      PlanType: t('Labels.Agreement.planType', { defaultValue: 'Plan Type' }),
      BillingMethod: t('Labels.Agreement.billingMethod', { defaultValue: 'Billing Method' }),
      Currency: t('Labels.Agreement.currency', { defaultValue: 'Currency' }),
      InvoiceDue: t('Labels.Agreement.invoiceDue', { defaultValue: 'Invoice Due' }),
      InvoiceDueDate: t('Labels.Agreement.invoiceDueDate', { defaultValue: 'Invoice Due Date' }),
      TermInMonths: t('Labels.Agreement.termInMonths', { defaultValue: 'Term in Months' }),
      SetupFee: t('Labels.Agreement.implementationFee', { defaultValue: 'Implementation Fee' }),
      PlatformLicenceFee: t('Labels.Agreement.platformLicenceFee', { defaultValue: 'Platform Licence Fee' }),
      PlatformLicenseFeeFrequency: t('Labels.Agreement.platformLicenseFeeFrequency', {
        defaultValue: 'Platform License Fee Frequency',
      }),
      NoPlatformLicenceFeePeriod: t('Labels.Agreement.noPlatformLicenceFeePeriod', {
        defaultValue: 'No Platform Licence Fee Period',
      }),
      MinimumProgramFee: t('Labels.Agreement.minimumProgramFee', { defaultValue: 'Minimum Program Fee' }),
      NoMinimumProgramFeePeriod: t('Labels.Agreement.noMinimumProgramFeePeriod', {
        defaultValue: 'No Minimum Program Fee Period',
      }),
      ReserveToActivate: t('Labels.Agreement.reserveToActivate', { defaultValue: 'Reserve to Activate' }),
      LegalAgreementNotes: t('Labels.Agreement.legalAgreementNotes', { defaultValue: 'Legal Agreement Notes' }),

      // Account configuration.
      SubAccountsEnabled: t('Labels.AccountConfiguration.subAccounts', { defaultValue: 'Sub-Accounts' }),
      SubAccountsFee: t('Labels.Agreement.subAccountsFee', { defaultValue: 'Sub-Accounts Fee' }),
      ClientAccountsEnabled: t('Labels.AccountConfiguration.clientAccounts', { defaultValue: 'Client Accounts' }),
      ClientAccountOnboardingFee: t('Labels.AccountConfiguration.clientAccountOnboardingFee', {
        defaultValue: 'Client Account Onboarding Fee',
      }),
      ClientAccountsFee: t('Labels.AccountConfiguration.clientAccountMonthlyFee', {
        defaultValue: 'Client Account Monthly Fee',
      }),
      IncludedClientAccounts: t('Labels.AccountConfiguration.numClientAccounts', {
        defaultValue: '# of Client Accounts',
      }),
      InterestAccountEnabled: t('Labels.AccountConfiguration.interestAccountEnabled', {
        defaultValue: 'Interest Account Enabled',
      }),
      PlatformUsage: t('Labels.AccountConfiguration.platformUsage', { defaultValue: 'Platform Usage' }),
      SystemOfRecord: t('Labels.AccountConfiguration.accountingSystem', { defaultValue: 'Accounting System' }),
      DataAggregator: t('Labels.AccountConfiguration.dataAggregation', { defaultValue: 'Data Aggregation' }),
    };

    // The flat billing-settings table (`BillingPackages/BillingSettingsList`) is keyed by the same
    // codes, so it reuses the agreement/config map and adds the settings-only codes. Two codes render
    // a different label in that raw table than in the account-configuration view, so they are
    // overridden here rather than shared.
    const billingSettingLabelsByCode: Record<keyof BillingSettings, string> = {
      ...settingLabelsByCode,
      AutomaticInvoicing: t('Labels.Agreement.automaticInvoicing', { defaultValue: 'Automatic Invoicing' }),
      InvoicingCycle: t('Labels.Agreement.invoicingCycle', { defaultValue: 'Invoicing Cycle' }),
      SubAccountBilling: t('Labels.Agreement.subAccountBilling', { defaultValue: 'Sub Account Billing' }),
      InvoicePaymentMethod: t('Labels.Agreement.invoicePaymentMethod', { defaultValue: 'Invoice Payment Method' }),
      SubAccountMinimumProgramFee: t('Labels.Agreement.subAccountMinimumProgramFee', {
        defaultValue: 'Sub Account Minimum Program Fee',
      }),
      UseProgressivePricing: t('Labels.Agreement.useProgressivePricing', { defaultValue: 'Use Progressive Pricing' }),
      BlendedReturnRatesEnabled: t('Labels.Agreement.blendedReturnRatesEnabled', {
        defaultValue: 'Blended Return Rates Enabled',
      }),
      // Overrides — the raw settings table names these differently than the account-configuration view.
      SubAccountsEnabled: t('Labels.Agreement.subAccountsEnabled', { defaultValue: 'Sub Accounts Enabled' }),
      ClientAccountsEnabled: t('Labels.Agreement.clientAccountsEnabled', { defaultValue: 'Client Accounts Enabled' }),
    };

    // Fee rows — a shared, finite vocabulary (`PlanFeeLabel`) reused across every rail. Exhaustive
    // over everything not in `UntranslatedFeeLabel`, so a forgotten translatable label fails `tsc`.
    const feeLabels: Record<Exclude<PlanFeeLabel, UntranslatedFeeLabel>, string> = {
      'Transaction Fee': t('Labels.Fees.transactionFee', { defaultValue: 'Transaction Fee' }),
      'Processing Fee': t('Labels.Fees.processingFee', { defaultValue: 'Processing Fee' }),
      Percentage: t('Labels.Fees.percentage', { defaultValue: 'Percentage' }),
      'Minimum Amount': t('Labels.Fees.minimumAmount', { defaultValue: 'Minimum Amount' }),
      'Maximum Amount': t('Labels.Fees.maximumAmount', { defaultValue: 'Maximum Amount' }),
      'Failed Transaction Fee': t('Labels.Fees.failedTransactionFee', { defaultValue: 'Failed Transaction Fee' }),
      'Cancellation Fee': t('Labels.Fees.cancellationFee', { defaultValue: 'Cancellation Fee' }),
      'Admin Error': t('Labels.Fees.adminError', { defaultValue: 'Admin Error' }),
      'Unauthorized Error': t('Labels.Fees.unauthorizedError', { defaultValue: 'Unauthorized Error' }),
      'NSF Fee': t('Labels.Fees.nsfFee', { defaultValue: 'NSF Fee' }),
      'Token Fee': t('Labels.Fees.tokenFee', { defaultValue: 'Token Fee' }),
      'Third Party Fee': t('Labels.Fees.thirdPartyFee', { defaultValue: 'Third Party Fee' }),
      'Token Minimum Amount': t('Labels.Fees.tokenMinimumAmount', { defaultValue: 'Token Minimum Amount' }),
      'Token Maximum Amount': t('Labels.Fees.tokenMaximumAmount', { defaultValue: 'Token Maximum Amount' }),
      'Third Party Minimum Amount': t('Labels.Fees.thirdPartyMinimumAmount', {
        defaultValue: 'Third Party Minimum Amount',
      }),
      'Third Party Maximum Amount': t('Labels.Fees.thirdPartyMaximumAmount', {
        defaultValue: 'Third Party Maximum Amount',
      }),
      'Funding Processing Fee': t('Labels.Fees.fundingProcessingFee', { defaultValue: 'Funding Processing Fee' }),
      // The backend really emits the double space (`'Withdraw  ' . $fee`).
      'Withdraw  Processing Fee': t('Labels.Fees.withdrawalProcessingFee', {
        defaultValue: 'Withdraw Processing Fee',
      }),
      'Linked Account Fee': t('Labels.Fees.linkedAccountFee', { defaultValue: 'Linked Account Fee' }),
      'T1 Release Monthly Fee': t('Labels.Fees.ttReleaseMonthlyFee', { defaultValue: 'T1 Release Monthly Fee' }),
      'Per Request': t('Labels.Fees.perRequest', { defaultValue: 'Per Request' }),
      'Additional Email Address': t('Labels.Fees.additionalEmailAddress', {
        defaultValue: 'Additional Email Address',
      }),
      'Chargeback Fee': t('Labels.Fees.chargebackFee', { defaultValue: 'Chargeback Fee' }),
      NSF: t('Labels.Fees.nsf', { defaultValue: 'NSF' }),
      'T+0 Percentage': t('Labels.Fees.t0Percentage', { defaultValue: 'T+0 Percentage' }),
      'T+1 Percentage': t('Labels.Fees.t1Percentage', { defaultValue: 'T+1 Percentage' }),
      'T+2 Percentage': t('Labels.Fees.t2Percentage', { defaultValue: 'T+2 Percentage' }),
      'Branding Email Transaction Fee': t('Labels.Fees.brandingEmailTransactionFee', {
        defaultValue: 'Branding Email Transaction Fee',
      }),
      'Branding eLinx Transaction Fee': t('Labels.Fees.brandingELinxTransactionFee', {
        defaultValue: 'Branding eLinx Transaction Fee',
      }),
      'Branding Portal Transaction Fee': t('Labels.Fees.brandingPortalTransactionFee', {
        defaultValue: 'Branding Portal Transaction Fee',
      }),
      'Branding Onboarding Transaction Fee': t('Labels.Fees.brandingOnboardingTransactionFee', {
        defaultValue: 'Branding Onboarding Transaction Fee',
      }),
      'Journal Entry Transaction Fee': t('Labels.Fees.journalEntryTransactionFee', {
        defaultValue: 'Journal Entry Transaction Fee',
      }),
      'VoPay 360 Enabled': t('Labels.Fees.voPay360Enabled', { defaultValue: 'VoPay 360 Enabled' }),
      'VoPay 360 Embedded Enabled': t('Labels.Fees.voPay360EmbeddedEnabled', {
        defaultValue: 'VoPay 360 Embedded Enabled',
      }),
      'VoPay 360 Integration Fee': t('Labels.Fees.voPay360IntegrationFee', {
        defaultValue: 'VoPay 360 Integration Fee',
      }),
      'ERP System': t('Labels.Fees.erpSystem', { defaultValue: 'ERP System' }),
      'Max Push To Card Amount': t('Labels.Fees.maxPushToCardAmount', { defaultValue: 'Max Push To Card Amount' }),
      'Daily Limit Inbound Amount': t('Labels.Fees.dailyLimitInboundAmount', {
        defaultValue: 'Daily Limit Inbound Amount',
      }),
      'Weekly Limit Inbound Amount': t('Labels.Fees.weeklyLimitInboundAmount', {
        defaultValue: 'Weekly Limit Inbound Amount',
      }),
      'Monthly Limit Inbound Amount': t('Labels.Fees.monthlyLimitInboundAmount', {
        defaultValue: 'Monthly Limit Inbound Amount',
      }),
      'Daily Limit Outbound Amount': t('Labels.Fees.dailyLimitOutboundAmount', {
        defaultValue: 'Daily Limit Outbound Amount',
      }),
      'Weekly Limit Outbound Amount': t('Labels.Fees.weeklyLimitOutboundAmount', {
        defaultValue: 'Weekly Limit Outbound Amount',
      }),
      'Monthly Limit Outbound Amount': t('Labels.Fees.monthlyLimitOutboundAmount', {
        defaultValue: 'Monthly Limit Outbound Amount',
      }),
      'Monthly Inbound Transaction Limit': t('Labels.Fees.monthlyInboundTransactionLimit', {
        defaultValue: 'Monthly Inbound Transaction Limit',
      }),
      'Monthly Outbound Transaction Limit': t('Labels.Fees.monthlyOutboundTransactionLimit', {
        defaultValue: 'Monthly Outbound Transaction Limit',
      }),
      'Business Account Transaction Limit': t('Labels.Fees.businessAccountTransactionLimit', {
        defaultValue: 'Business Account Transaction Limit',
      }),
      'Individual Account Transaction Limit': t('Labels.Fees.individualAccountTransactionLimit', {
        defaultValue: 'Individual Account Transaction Limit',
      }),
      'ReceiveOnly Account Transaction Limit': t('Labels.Fees.receiveOnlyAccountTransactionLimit', {
        defaultValue: 'ReceiveOnly Account Transaction Limit',
      }),
      'Standard Account Transaction Limit': t('Labels.Fees.standardAccountTransactionLimit', {
        defaultValue: 'Standard Account Transaction Limit',
      }),
    };

    // Rail / service section names (`PlanServiceName` — the section IS the API's object key).
    // Exhaustive over everything not in `UntranslatedServiceName`, so a new member forces a choice.
    const serviceNames: Record<Exclude<PlanServiceName, UntranslatedServiceName>, string> = {
      // Account settings ("My Account") section names. The API sends "Withdraw My Account" (no
      // "VoPay"); the locale leaf keeps its original name.
      'Fund My VoPay Account': t('Labels.AccountSettings.fundMyVoPayAccount', {
        defaultValue: 'Fund My VoPay Account',
      }),
      'Withdraw My Account': t('Labels.AccountSettings.withdrawMyVoPayAccount', {
        defaultValue: 'Withdraw My VoPay Account',
      }),
      'EFT Withdraw': t('Labels.Rails.eftWithdraw', { defaultValue: 'EFT Withdraw' }),
      'EFT Funding': t('Labels.Rails.eftFunding', { defaultValue: 'EFT Funding' }),
      'Debit Card Push (OCT)': t('Labels.Rails.debitCardPushOCT', { defaultValue: 'Debit Card Push (OCT)' }),
      'Debit Card Pull (AFT)': t('Labels.Rails.debitCardPullAFT', { defaultValue: 'Debit Card Pull (AFT)' }),
      'Bill Pay': t('Labels.Rails.billPay', { defaultValue: 'Bill Pay' }),
      'Journal Entry': t('Labels.Rails.journalEntry', { defaultValue: 'Journal Entry' }),
      'ACH Funding': t('Labels.Rails.achFunding', { defaultValue: 'ACH Funding' }),
      'Same Day ACH Funding': t('Labels.Rails.sameDayACHFunding', { defaultValue: 'Same Day ACH Funding' }),
      'ACH Withdraw': t('Labels.Rails.achWithdraw', { defaultValue: 'ACH Withdraw' }),
      'Same Day ACH Withdraw': t('Labels.Rails.sameDayACHWithdraw', { defaultValue: 'Same Day ACH Withdraw' }),
      'Fedwire Funding': t('Labels.Rails.fedwireFunding', { defaultValue: 'Fedwire Funding' }),
      'Fedwire Withdraw': t('Labels.Rails.fedwireWithdraw', { defaultValue: 'Fedwire Withdraw' }),
      'RTP Withdraw': t('Labels.Rails.rtpWithdraw', { defaultValue: 'RTP Withdraw' }),
      'Fednow Withdraw': t('Labels.Rails.fednowWithdraw', { defaultValue: 'Fednow Withdraw' }),
      'Crossborder Withdraw': t('Labels.Rails.crossborderWithdraw', { defaultValue: 'Crossborder Withdraw' }),
      'Fund My Account': t('Labels.Rails.fundMyAccount', { defaultValue: 'Fund My Account' }),
      'Client Accounts Transaction': t('Labels.Rails.clientAccountsTransaction', {
        defaultValue: 'Client Accounts Transaction',
      }),
      'Tax Remittance': t('Labels.Rails.taxRemittance', { defaultValue: 'Tax Remittance' }),
      'NOC Fee': t('Labels.Rails.nocFee', { defaultValue: 'NOC Fee' }),
      'Journal Entry Fee': t('Labels.Rails.journalEntryFee', { defaultValue: 'Journal Entry Fee' }),
      'Transaction Amount Limits': t('Labels.Rails.transactionAmountLimits', {
        defaultValue: 'Transaction Amount Limits',
      }),
      'Transaction Creation Limits': t('Labels.Rails.transactionCreationLimits', {
        defaultValue: 'Transaction Creation Limits',
      }),
      'Inbound Transaction Amount Limits': t('Labels.Rails.inboundTransactionAmountLimits', {
        defaultValue: 'Inbound Transaction Amount Limits',
      }),
      'Outbound Transaction Amount Limits': t('Labels.Rails.outboundTransactionAmountLimits', {
        defaultValue: 'Outbound Transaction Amount Limits',
      }),
      'Inbound Transaction Creation Limits': t('Labels.Rails.inboundTransactionCreationLimits', {
        defaultValue: 'Inbound Transaction Creation Limits',
      }),
      'Outbound Transaction Creation Limits': t('Labels.Rails.outboundTransactionCreationLimits', {
        defaultValue: 'Outbound Transaction Creation Limits',
      }),
      'Basic Card Verifications': t('Labels.Rails.basicCardVerifications', {
        defaultValue: 'Basic Card Verifications',
      }),
      'Enhanced Card Verifications': t('Labels.Rails.enhancedCardVerifications', {
        defaultValue: 'Enhanced Card Verifications',
      }),
      'Basic Email Address Verifications': t('Labels.Rails.basicEmailAddressVerifications', {
        defaultValue: 'Basic Email Address Verifications',
      }),
      'Enhanced Email Address Verifications': t('Labels.Rails.enhancedEmailAddressVerifications', {
        defaultValue: 'Enhanced Email Address Verifications',
      }),
      'Basic Phone Number Verifications': t('Labels.Rails.basicPhoneNumberVerifications', {
        defaultValue: 'Basic Phone Number Verifications',
      }),
      'Enhanced Phone Number Verifications': t('Labels.Rails.enhancedPhoneNumberVerifications', {
        defaultValue: 'Enhanced Phone Number Verifications',
      }),
      'Non-Doc ID Verifications': t('Labels.Rails.nonDocIDVerifications', {
        defaultValue: 'Non-Doc ID Verifications',
      }),
      'Address Verifications': t('Labels.Rails.addressVerifications', { defaultValue: 'Address Verifications' }),
      'ID Basic Verifications': t('Labels.Rails.idBasicVerifications', { defaultValue: 'ID Basic Verifications' }),
      'ID Enhanced Verifications': t('Labels.Rails.idEnhancedVerifications', {
        defaultValue: 'ID Enhanced Verifications',
      }),
      'Name Screen Verifications': t('Labels.Rails.nameScreenVerifications', {
        defaultValue: 'Name Screen Verifications',
      }),
    };

    const values: Record<DisplayValueKey, string> = {
      'Pay As You Go': t('Values.payAsYouGo', { defaultValue: 'Pay As You Go' }),
      Subscription: t('Values.subscription', { defaultValue: 'Subscription' }),
      'Post Paid': t('Values.postPaid', { defaultValue: 'Post Paid' }),
      'Per Transaction': t('Values.perTransaction', { defaultValue: 'Per Transaction' }),
      Specific: t('Values.specific', { defaultValue: 'Specific' }),
      'Specific Day of the Month': t('Values.specificDayOfTheMonth', { defaultValue: 'Specific Day of the Month' }),
      'Beginning of the Month': t('Values.beginningOfTheMonth', { defaultValue: 'Beginning of the Month' }),
      'End of the Month': t('Values.endOfTheMonth', { defaultValue: 'End of the Month' }),
      Monthly: t('Values.monthly', { defaultValue: 'Monthly' }),
      Yearly: t('Values.yearly', { defaultValue: 'Yearly' }),
      'Per Request': t('Values.perRequest', { defaultValue: 'Per Request' }),
      'Not Applied': t('Values.notApplied', { defaultValue: 'Not Applied' }),
      // `InvoicePaymentMethod` arrives as the lowercased 'vopay account', hence the flat capitalization.
      'Vopay Account': t('Values.vopayAccount', { defaultValue: 'Vopay Account' }),
    };

    // One lookup over all the prose vocabularies, keyed on their union rather than `string` so a call
    // site can only pass a value the API can actually emit.
    const translationByEnglishLabel = new Map<PlanFeeLabel | PlanServiceName | PlanTierLabel, string>([
      ...objectEntries(feeLabels),
      ...objectEntries(serviceNames),
    ]);

    const translationByEnglishValue = new Map<string, string>(Object.entries(values));

    return {
      /** Falls back to the b/e label if the key hasn't yet been added to `BillingSettingCode`. */
      resolveSettingLabel: (code: BillingSettingCode, fallback: string): string =>
        settingLabelsByCode[code] ?? fallback,

      /** Falls back to a de-PascalCased code if the key hasn't yet been added to `BillingSettings`. */
      resolveSettingKeyLabel: (code: keyof BillingSettings): string =>
        billingSettingLabelsByCode[code] ?? code.split(/(?=[A-Z])/).join(' '),

      // Fee rows, rail / account-settings section names and runtime-built tier labels — prose-keyed
      // (see the header comment), typed to the vocabularies the API can emit.
      /** Falls back to the given English label if it isn't mapped (e.g. wordmarks), rendering it verbatim. */
      resolveLabel: (label: PlanFeeLabel | PlanServiceName | PlanTierLabel): string =>
        translationByEnglishLabel.get(label) ?? label,

      // Select display values (Pay-As-You-Go / Subscription / frequency …) — prose-keyed. The option
      // key is a numeric `JSON_FORCE_OBJECT` index, not a usable code, so the display value is the key.
      /** Falls back to the given English value if it isn't in `values`. */
      resolveValue: (englishValue: string): string => translationByEnglishValue.get(englishValue) ?? englishValue,
    };
  }, [t]);
}
